import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startupService } from "@/services/api/startup.service";
import { financialKeys } from "./useFinancialData";

export const teamKeys = {
  members: (startupId: string) => ["startups", startupId, "members"] as const,
  pendingInvites: () => ["invites", "pending"] as const,
};

export function useStartupMembers(startupId: string, enabled = true) {
  return useQuery({
    queryKey: teamKeys.members(startupId),
    queryFn: () => startupService.getMembers(startupId),
    enabled: !!startupId && enabled,
  });
}

export function usePendingInvites(enabled = true) {
  return useQuery({
    queryKey: teamKeys.pendingInvites(),
    queryFn: () => startupService.getPendingInvites(),
    enabled,
  });
}

export function useInviteStartupMember(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, role }: { email: string; role?: string }) =>
      startupService.inviteMember(startupId, email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members(startupId) });
      queryClient.invalidateQueries({ queryKey: [...financialKeys.all, "startups"] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useRemoveStartupMember(startupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberUserId: string) =>
      startupService.removeMember(startupId, memberUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members(startupId) });
      queryClient.invalidateQueries({ queryKey: [...financialKeys.all, "startups"] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
}

export function useAcceptStartupInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (startupId: string) => startupService.acceptInvite(startupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.pendingInvites() });
      queryClient.invalidateQueries({ queryKey: [...financialKeys.all, "startups"] });
    },
  });
}
